// ─────────────────────────────────────────────────────────────
// MARTIAL ARTS KIDS CLASS READY™ — all copy and links live here. Image URLs live in data/images.ts, the pixel in data/pixel.ts.
// v2 (23/10): the main product is now the COMPLETE YEAR: 52 complete classes, one for every week, in 5 volumes
// (Vol. 01 = year map + the 10 key ★ classes; Vol. 02–05 = one volume per quarter). Same $27, same checkout, same 5 bonuses.
// Promise (must match ads, checkout and the PDF): 52 complete 45-minute kids classes, $27 one-time, 10-day guarantee.
// Never say "10 classes" as the total: 10 is only what Vol. 01 holds.
// Values = the checkout: main $159; bonuses $37, $27, $27, $27, $37 ($155); total $314 → $27, you save $287.
// Kids + contact sport: no promises about self-defense, belts or sign-ups; pads and air only, no sparring; no testimonials.
// ─────────────────────────────────────────────────────────────

export { META_PIXEL_ID } from './pixel';
export const CHECKOUT_URL = 'https://martial-arts-kids-ready.impultienda.ar/checkout';

export const PAGE_TITLE = 'Martial Arts Kids Class Ready™ | Your Whole Year of Kids Classes, Already Planned';
export const META_DESCRIPTION =
  '52 complete, ready-to-teach 45-minute kids martial arts classes, one for every week of the year. Open this week’s class, prepare in 5 minutes and teach it tonight.';

// ── GLOBAL ───────────────────────────────────────────────────
export const brand = 'MARTIAL ARTS KIDS CLASS READY™';
/** Label for every button that scrolls to the offer section */
export const ctaLabel = 'PLAN MY WHOLE YEAR';
export const ctaMicrocopy = '52 complete classes · One-time $27 · 10-day guarantee';
export const stickyLabel = 'PLAN MY WHOLE YEAR · $27';
export const topBarMobileLabel = '52 CLASSES · $27';
export const price = '$27';

// ── THE 52 WEEKS (from the 5 volumes) ─────────────────────────
// kind: 'key' = one of the 10 key classes (★, Vol. 01) · 'class' = quarter class (Vol. 02–05) · 'check' = stripe check (◆)
type Kind = 'key' | 'class' | 'check';
const W: [number, string, string, Kind][] = [
  [1, 'Stance, Guard & Dojo Respect', 'Respect', 'key'],
  [2, 'Stance & Guard', 'Listening', 'class'],
  [3, 'Dojo Respect', 'Courtesy', 'class'],
  [4, 'Stance & Balance', 'Patience', 'class'],
  [5, 'Straight Punches: Jab & Cross', 'Focus', 'key'],
  [6, 'Jab', 'Effort', 'class'],
  [7, 'Cross', 'Determination', 'class'],
  [8, 'Jab-Cross', 'Encouragement', 'class'],
  [9, 'Front Kick & Balance', 'Self-Control', 'key'],
  [10, 'Balance', 'Calm', 'class'],
  [11, 'Front Kick', 'Goal Setting', 'class'],
  [12, 'Front Kick on Target', 'Honesty', 'class'],
  [13, 'Stripe Check', 'Integrity', 'check'],
  [14, 'Roundhouse Kick', 'Perseverance', 'key'],
  [15, 'Roundhouse Kick: The Pivot', 'Responsibility', 'class'],
  [16, 'Roundhouse Kick: Low & Middle', 'Kindness', 'class'],
  [17, 'Kick Combo', 'Positive Attitude', 'class'],
  [18, 'Blocks: High, Middle & Low', 'Confidence', 'key'],
  [19, 'High Block', 'Bravery', 'class'],
  [20, 'Middle & Low Block', 'Fairness', 'class'],
  [21, 'Block & Counter', 'Self-Respect', 'class'],
  [22, 'Punch Combinations on Pads', 'Teamwork', 'key'],
  [23, 'Hook', 'Helpfulness', 'class'],
  [24, 'Uppercut', 'Hard Work', 'class'],
  [25, 'Combinations', 'Cooperation', 'class'],
  [26, 'Stripe Check', 'Humility', 'check'],
  [27, 'Footwork & Distance', 'Awareness', 'key'],
  [28, 'Footwork', 'Being Prepared', 'class'],
  [29, 'Distance', 'Personal Space', 'class'],
  [30, 'Angles', 'Problem Solving', 'class'],
  [31, 'Safe Falling', 'Courage', 'key'],
  [32, 'Back Fall', 'Trust', 'class'],
  [33, 'Side Fall', 'Self-Belief', 'class'],
  [34, 'Get Up Safely', 'Resilience', 'class'],
  [35, 'Grab Escapes & Stranger Safety', 'Safety', 'key'],
  [36, 'Wrist Grab Escape', 'Calm', 'class'],
  [37, 'Strong Voice', 'Speaking Up', 'class'],
  [38, 'Safety Habits', 'Good Choices', 'class'],
  [39, 'Stripe Check', 'Consistency', 'check'],
  [40, 'Belt Test Rehearsal', 'Discipline', 'key'],
  [41, 'Review Stance & Punches', 'Respect', 'class'],
  [42, 'Review Kicks', 'Self-Control', 'class'],
  [43, 'Review Blocks', 'Focus', 'class'],
  [44, 'Punch-Kick Combinations', 'Perseverance', 'class'],
  [45, 'Footwork & Combinations', 'Awareness', 'class'],
  [46, 'Falls & Escapes', 'Courage', 'class'],
  [47, 'Short Form', 'Excellence', 'class'],
  [48, 'Focus & Confidence', 'Confidence', 'class'],
  [49, 'Leadership', 'Leadership', 'class'],
  [50, 'Teamwork', 'Teamwork', 'class'],
  [51, 'Family Class', 'Gratitude', 'class'],
  [52, 'Year-End Stripe Check', 'Growth', 'check'],
];
export type Week = { week: number; title: string; value: string; kind: Kind; /** 0 = Vol. 01 … 4 = Vol. 05 */ vol: number };
export const weeks: Week[] = W.map(([week, title, value, kind]) => ({
  week,
  title,
  value,
  kind,
  vol: kind === 'key' ? 0 : Math.ceil(week / 13),
}));

// ── 1. HERO (buyer line, title, short subtitle, mockup, button) ──
export const hero = {
  eyebrow: 'FOR KARATE, TAEKWONDO, KICKBOXING & MUAY THAI INSTRUCTORS',
  titleLead: 'YOUR WHOLE YEAR OF KIDS CLASSES,',
  titleHighlight: 'ALREADY PLANNED.',
  sub: '52 complete, ready-to-teach 45-minute classes for kids — one for every week of the year. Open this week’s class and teach it tonight.',
  mockupAlt: 'The 5 volumes of Martial Arts Kids Class Ready™ on a desktop screen, with the 5 bonuses on tablets and phones',
};

// ── 2. STATS ─────────────────────────────────────────────────────
export const stats = [
  { value: '52', label: 'COMPLETE CLASSES' },
  { value: '5', label: 'VOLUMES, ONE FULL YEAR' },
  { value: '45', label: 'MINUTES, FULLY TIMED' },
  { value: '3', label: 'AGE LEVELS IN EVERY CLASS' },
];

// ── 2b. BEFORE / AFTER ──────────────────────────────────────────────
export const beforeAfter = {
  title: 'THE NIGHT BEFORE VS. ON THE MAT.',
  before: {
    label: 'BEFORE',
    when: 'The night before',
    quote: '“What do I teach the kids tonight?”',
    items: ['Searching videos for drills.', 'The same punching drill again.', 'Little ones lost, older kids bored.'],
  },
  after: {
    label: 'AFTER',
    when: 'On the mat',
    items: ['Open this week’s class.', 'Prepare in 5 minutes.', 'Teach the 45-minute class.', 'Bow out and send the parent note.'],
  },
};

// ── 3. THE 5 VOLUMES (new in v2: replaces the 52-week plan and the list of 10 classes) ──
export const volumes = {
  eyebrow: '52 COMPLETE CLASSES · 5 VOLUMES',
  title: 'EVERY WEEK OF THE YEAR HAS ITS OWN CLASS.',
  sub: 'Not a plan with blanks to fill. All 52 weeks are complete 45-minute classes, written out step by step and ready to teach.',
  fanAlt: 'The 5 volumes of Martial Arts Kids Class Ready™',
  gridTitle: '52 WEEKS · 52 CLASSES',
  gridHint: 'Tap any week to see its volume.',
  legend: { key: 'Key class (Vol. 01)', class: 'Quarter class (Vol. 02–05)', check: 'Stripe check' },
  tablistLabel: 'The 5 volumes',
  classesLabel: 'THE CLASSES',
  includesLabel: 'ALSO INSIDE',
  items: [
    {
      tab: 'VOL. 01',
      label: 'VOLUME 01 · THE YEAR MAP',
      title: 'The Year Map + 10 Key Classes',
      meta: '10 classes · Weeks 1, 5, 9, 14, 18, 22, 27, 31, 35 and 40',
      text: 'Your map of the year and the 10 key classes that open every block: stance, punches, kicks, blocks, combinations, footwork, falls, safety and the belt test rehearsal.',
      includes: ['The 52-week plan: every week points to its class', 'When the class gets hard: what to say and do', 'Class planner + stripe tracker', 'Field card to keep next to the mat'],
    },
    {
      tab: 'VOL. 02',
      label: 'VOLUME 02 · QUARTER 1',
      title: 'Build the Base',
      meta: '10 classes · Weeks 2–13',
      text: 'Stance, guard, dojo respect, the jab, the cross and the front kick, week by week, closing with the Quarter 1 stripe check.',
      includes: ['Every week of Quarter 1, class by class', '6 new warm-up games', 'What kids learn this quarter, by age', 'Stripe tracker + field card'],
    },
    {
      tab: 'VOL. 03',
      label: 'VOLUME 03 · QUARTER 2',
      title: 'Strike & Defend',
      meta: '10 classes · Weeks 15–26',
      text: 'The roundhouse kick, blocks, the hook, the uppercut and combinations on pads, closing with the Quarter 2 stripe check.',
      includes: ['Every week of Quarter 2, class by class', '6 new warm-up games', 'What kids learn this quarter, by age', 'Stripe tracker + field card'],
    },
    {
      tab: 'VOL. 04',
      label: 'VOLUME 04 · QUARTER 3',
      title: 'Move & Stay Safe',
      meta: '10 classes · Weeks 28–39',
      text: 'Footwork, distance, angles, safe falls, gentle grab escapes, a strong voice and safety habits, closing with the Quarter 3 stripe check.',
      includes: ['Every week of Quarter 3, class by class', '6 new warm-up games', 'Safety rules for falls and role-play', 'Stripe tracker + field card'],
    },
    {
      tab: 'VOL. 05',
      label: 'VOLUME 05 · QUARTER 4',
      title: 'Put It All Together',
      meta: '12 classes · Weeks 41–52',
      text: 'Review weeks, punch-kick combinations, a short form, leadership, a family class and the year-end stripe check with a ceremony.',
      includes: ['Every week of Quarter 4, class by class', '6 new warm-up games', 'Year-end stripe tracker', 'Field card for the last quarter'],
    },
  ],
  cta: 'GET ALL 52 CLASSES',
  ctaMicrocopy: '+ 5 bonuses · $27 USD · One-time payment',
};

// ── 4. WHAT DOES YOUR CLASS NEED THIS WEEK (pick a need, see the class that is ready for it) ──
const block = 'Class block: bow-in & warm-up game 8 · technique 12 · pads & partners 12 · final game 8 · value talk 5';
export const selector = {
  eyebrow: 'START WHERE YOUR STUDENTS ARE',
  title: 'WHAT DOES YOUR CLASS NEED THIS WEEK?',
  sub: 'Pick a need and see the class that is ready for it.',
  startLabel: 'YOUR NEXT CLASS',
  tablistLabel: 'Class needs',
  columns: ['PAGE 1 · CLASS PLAN', 'PAGE 2 · TEACH IT', 'PAGE 3 · DRILLS & GAME'],
  note: 'All 52 classes follow this same 3-page structure. Adapt each one to your style and your school’s safety rules.',
  items: [
    {
      tab: 'A strong start: stance & respect',
      n: 'WEEK 1 · VOL. 01',
      title: 'Stance, Guard & Dojo Respect',
      goal: 'The child bows in, stands in a strong fighting stance with the guard up and returns to ready stance on command.',
      open: ['Class goal, equipment and 5-minute prep', 'Your opening words: “Feet apart, one foot back. Hands up by your face. That’s your guard!”', block],
      activity: ['5 technique steps with key points: attention, bow, ready stance, fighting stance, guard', '3 Fix It cues, like “Train tracks, not a tightrope!”', 'Age level card: how to teach it to ages 4–6, 7–9 and 10–12'],
      close: ['2 drills with mat diagrams: Stance Check Line and Bounce & Freeze', 'Final game: Dojo Says', 'Value talk (Respect), parent note and stripe check'],
    },
    {
      tab: 'Kicks with balance',
      n: 'WEEK 9 · VOL. 01',
      title: 'Front Kick & Balance',
      goal: 'The child lifts the knee, kicks forward with the ball of the foot and puts the foot down in stance with control.',
      open: ['Class goal, equipment and 5-minute prep', 'Your opening words: “Knee up… kick… knee back… foot down. Guard stays up!”', block],
      activity: ['Technique steps with key points, from knee-up to foot down', '3 Fix It cues for the most common kicking errors', 'Age level card for ages 4–6, 7–9 and 10–12'],
      close: ['2 drills on the kick shield, with mat diagrams and timing', 'Final game: Stoplight Kicks', 'Value talk (Self-Control), parent note and stripe check'],
    },
    {
      tab: 'Combinations on the pads',
      n: 'WEEK 22 · VOL. 01',
      title: 'Punch Combinations on Pads',
      goal: 'The child throws a 3-punch combination (jab, cross, hook) on a pad and returns to guard.',
      open: ['Class goal, equipment and 5-minute prep', 'Your opening words: “1 is jab, 2 is cross, 3 is hook. Ready? 1-2-3… guard!”', block],
      activity: ['Technique steps for the hook and the 1-2-3', 'How older kids hold the mitts safely for a partner', 'Age level card for ages 4–6, 7–9 and 10–12'],
      close: ['2 pad drills with mat diagrams, rounds and timing', 'A final game that builds the combination', 'Value talk (Teamwork), parent note and stripe check'],
    },
    {
      tab: 'Stranger safety & escapes',
      n: 'WEEK 35 · VOL. 01',
      title: 'Grab Escapes & Stranger Safety',
      goal: 'The child escapes a gentle wrist grab, steps back, uses a strong voice and knows to run to and tell a trusted adult.',
      open: ['Class goal, equipment and 5-minute prep', 'Your opening words: escape, step back, strong voice, run to a safe adult', block],
      activity: ['Simple escape steps, practiced slowly and gently', 'The safety habits: stay with your group, use your voice, tell a trusted adult', 'Age level card for ages 4–6, 7–9 and 10–12'],
      close: ['2 calm role-play drills with mat diagrams', 'Final game: Strong Voice Relay', 'Value talk (Safety), parent note and stripe check'],
    },
  ],
};

// ── 5. LOOK INSIDE (real pages of the volumes, in the same order as pageImages in data/images.ts) ──
export const lookInside = {
  eyebrow: 'REAL PAGES. SEE THE FORMAT BEFORE YOU BUY.',
  title: 'SEE WHAT YOU’LL HAVE NEXT TO THE MAT.',
  sub: 'Open any page and read it at full size: the index of all 52 weeks, the year plan, a complete class in 3 pages and real classes from all 5 volumes.',
  hint: 'Swipe to see more · Tap any page to view it full size',
  note: 'These are selected pages, not the full book.',
  captions: [
    'The Complete Year · index of all 52 weeks',
    'Vol. 01 · The year plan, Quarter 1',
    'Week 5 · class plan',
    'Week 5 · teach it',
    'Week 5 · drills & game',
    'Vol. 03 · Week 15: the roundhouse pivot',
    'Vol. 04 · Week 29: distance',
    'Vol. 05 · Week 49: leadership',
    'Vol. 05 · Every week of Quarter 4',
    'Vol. 01 · Class planner + stripe tracker',
  ],
};

// ── 6. THE SYSTEM ──────────────────────────────
export const plan = {
  eyebrow: 'ONE SYSTEM FOR EVERY CLASS',
  title: 'THE WHOLE YEAR IS READY. YOU JUST BOW IN AND TEACH.',
  text: 'Every week of the year has its own complete class. Each one gives you the plan, the technique steps and the drills & game in 3 pages.',
  steps: [
    { title: 'OPEN', text: 'Find this week’s class.' },
    { title: 'PREPARE', text: 'Read the 3 pages and set up the mat in 5 minutes.' },
    { title: 'TEACH', text: 'Warm-up game, one technique, pads and partners.' },
    { title: 'BOW OUT', text: 'Final game, value talk and the parent note.' },
  ],
  featuresTitle: 'IN EVERY ONE OF THE 52 CLASSES',
  features: [
    { icon: 'goal', title: 'One clear class goal', text: 'One sentence of what the kids will do, the week to teach it and what they need first.' },
    { icon: 'timer', title: 'A timed 45-minute class', text: 'Like bow-in & warm-up game 8 · technique 12 · pads & partners 12 · final game 8 · value talk 5.' },
    { icon: 'steps', title: 'Technique steps + Fix It cues', text: 'Numbered steps with key points, and the exact cue to fix the most common errors.' },
    { icon: 'questions', title: 'Ages 4–6, 7–9 and 10–12', text: 'How to teach the same class to every age on your mat, even in a mixed group.' },
    { icon: 'quote', title: 'Your words, word for word', text: 'Opening words, commands and the value talk, plus a pro tip and a common mistake.' },
    { icon: 'action', title: 'Drills, game + parent note', text: '2 drills and a final game with mat diagrams, a stripe check and a note for parents.' },
  ],
} as const;

// ── 6b. VALUES (one value talk every week) ──────────────────
export const valuesBand = {
  eyebrow: 'MORE THAN KICKS AND PUNCHES',
  title: 'A VALUE FOR EVERY WEEK. ALL YEAR LONG.',
  sub: 'Every class closes with a short value talk, word for word, so parents see what their kids bring home.',
  /** How many value chips to show before the "+ N more" chip */
  show: 18,
  moreLabel: 'more',
};

// ── 7. BONUSES (same order and values as the checkout) ──────────────
export const bonuses = {
  eyebrow: '5 BONUSES INCLUDED WITH YOUR 52 CLASSES',
  title: 'EVERYTHING ELSE YOUR KIDS CLASSES NEED.',
  sub: 'Games, belt tests, parent messages, pad drills and a trial class plan, ready to use.',
  free: 'FREE',
  totalNote: '$155 in bonuses, included free',
  items: [
    {
      label: 'BONUS 01',
      value: '$37',
      short: '60 Martial Arts Games for Kids',
      hook: 'Never run out of games again.',
      title: '60 Martial Arts Games for Kids — No Equipment Needed',
      text: '60 fun, safe games to open, energize and close any class, organized by goal: warm-up, reaction, balance, focus, kicking and striking, teamwork and cool-down. Each with setup, rules, time and a safety tip.',
    },
    {
      label: 'BONUS 02',
      value: '$27',
      short: 'Belt Testing Kit',
      hook: 'Run calm, organized belt tests.',
      title: 'Belt Testing Kit — Requirements, Test Day Plan & Printable Certificates',
      text: 'A sample requirements checklist for every belt (adapt it to your style), a scoring sheet, a test day run sheet with timing, what to say to open and close the test, a stripe tracker and printable certificates.',
    },
    {
      label: 'BONUS 03',
      value: '$27',
      short: 'Parent Communication Pack',
      hook: 'Know exactly what to send to families.',
      title: 'Parent Communication Pack — Welcome Letter, Dojo Rules & Ready-to-Send Messages',
      text: 'A welcome letter for new students, one-page dojo rules for kids, a what-to-bring checklist, belt test announcements, a monthly values note, messages for missed classes and calm scripts for parent talks.',
    },
    {
      label: 'BONUS 04',
      value: '$27',
      short: '50 Pad & Partner Drills',
      hook: 'Fresh pad work for every class.',
      title: '50 Pad & Partner Drills — Ready for Any Kids Class',
      text: '50 drills with focus mitts, kick shields and partners for punches, kicks, blocks, combinations, footwork and reaction. Each with setup, rounds and timing, how to hold the pad and a safety note.',
    },
    {
      label: 'BONUS 05',
      value: '$37',
      short: 'The Trial Class Playbook',
      hook: 'Make every first class count.',
      title: 'The Trial Class Playbook — Run a First Class Kids and Parents Remember',
      text: 'How to welcome the family at the door, a 30-minute trial class script with games and a first technique every child can do, what to say at the end and ready-to-send follow-up messages.',
    },
  ],
};

// ── 8. OFFER ─────────────────────────────────────────────────────
export const offer = {
  eyebrow: 'YOUR NEXT CLASS STARTS HERE',
  title: 'YOUR WHOLE YEAR OF KIDS CLASSES. ALL 52, READY TO TEACH.',
  kicker: 'THE COMPLETE YEAR · 5 VOLUMES',
  name: 'Martial Arts Kids Class Ready™',
  description: '52 complete, ready-to-teach 45-minute kids classes, one for every week of the year, plus five bonuses: games, a belt testing kit, parent messages, pad drills and a trial class playbook.',
  mockupAlt: 'Everything included in Martial Arts Kids Class Ready™: the 5 volumes and the 5 bonuses',
  includedTitle: 'YOUR YEAR INCLUDES',
  included: [
    '52 complete classes: one for every week',
    '5 volumes in one 201-page PDF, with a tap-to-jump index',
    '4 stripe-check classes, the last with a ceremony',
    'Technique steps, key points and Fix It cues',
    'Ages 4–6, 7–9 and 10–12 in every class',
    'Drills, final game, value talk and parent note',
    'Instructor tools: planners, stripe trackers, field cards',
  ],
  /** Value stack: exactly the checkout prices */
  stackTitle: 'EVERYTHING YOU GET',
  mainItem: { label: 'Martial Arts Kids Class Ready™ — all 52 classes in 5 volumes', value: '$159' },
  /** The 5 volumes, listed under the main item (no separate prices: together they are the $159 main product) */
  volumeLines: [
    'Vol. 01 · Year map + 10 key classes',
    'Vol. 02 · Q1 Build the Base · 10 classes',
    'Vol. 03 · Q2 Strike & Defend · 10 classes',
    'Vol. 04 · Q3 Move & Stay Safe · 10 classes',
    'Vol. 05 · Q4 Put It All Together · 12 classes',
  ],
  totalLabel: 'Total value',
  totalValue: '$314',
  todayLabel: 'TODAY',
  priceNote: 'One-time payment · About 52¢ a class',
  saveNote: 'You save $287',
  /** The ONLY button that goes to checkout */
  cta: 'YES, SEND ME ALL 52 CLASSES — $27',
  ctaNote: ['Continue to secure checkout', 'Access sent by email after payment'],
  guaranteeTitle: '10-day money-back guarantee',
  guaranteeText: 'Teach your next classes with it. If it isn’t right for you, email us within 10 days and get your money back. No questions asked.',
  format: 'One English-language PDF, 201 pages, with a tap-to-jump index. Digital delivery. Adapt every class to your style, your school’s safety rules and the age and level of your students.',
};

// ── 9. GUARANTEE ────────────────────────────────────────────
export const guarantee = {
  sealTop: '10-DAY',
  sealBottom: 'MONEY-BACK',
  title: '10-DAY MONEY-BACK. NO QUESTIONS ASKED.',
  text: "Teach your next classes with it. If it isn't right for you, email support@stockdigital.shop within 10 days of purchase and we'll give your money back.",
};

// ── 10. FAQ ───────────────────────────────────────────────────────
export const faq = {
  eyebrow: 'KNOW EXACTLY WHAT YOU’RE GETTING',
  title: 'A FEW QUESTIONS BEFORE YOU BOW IN.',
  sub: 'Built for instructors and school owners who teach kids. A ready-to-teach class book, not a video course.',
  items: [
    {
      q: 'What exactly is this product?',
      a: 'One English-language PDF (201 pages, with a tap-to-jump index) with 52 complete, ready-to-teach 45-minute kids classes, one for every week of the year, in 5 volumes: the year plan with the 10 key classes, plus one volume per quarter. It also has instructor tools (planners, stripe trackers, field cards) and 5 bonuses.',
    },
    {
      q: 'Is it really the whole year?',
      a: 'Yes. Every one of the 52 weeks has its own complete class: 48 technique classes, 3 stripe-check classes and a year-end stripe check with a ceremony. There is nothing left to fill in.',
    },
    {
      q: 'Which styles does it work for?',
      a: 'Striking styles taught to kids: karate, taekwondo, kickboxing, Muay Thai and mixed-style programs. Techniques use general names (front kick, jab, high block), and every class reminds you to use your style’s names, etiquette and belt order.',
    },
    {
      q: 'What ages is it for?',
      a: 'Kids from about 4 to 12. Every class has an age level card with how to teach it to ages 4–6, 7–9 and 10–12, so it works for mixed-age groups too.',
    },
    {
      q: 'Is there sparring or contact?',
      a: 'No sparring. Kids strike pads, targets and the air. Partner drills use light, controlled contact on pads only, always supervised, and falls are taught on mats from low positions.',
    },
    {
      q: 'Do the safety classes teach self-defense?',
      a: 'The safety weeks (35 to 38) teach simple, gentle grab escapes and safety habits: stay with your group, use a strong voice and tell a trusted adult. It is not a promise that a child can defend themselves in any situation.',
    },
    {
      q: 'How do I receive it?',
      a: 'After your payment is approved, access is sent to the email address you enter at checkout. Nothing is shipped. Use an email you can access and check your spam folder if needed.',
    },
    {
      q: 'Can I use the classes individually?',
      a: 'Yes. Each class stands alone, so you can follow the year from week 1 or open the class your students need now.',
    },
    {
      q: 'Can I print it?',
      a: 'Yes. The pages are easy to read on a phone or tablet next to the mat, and the class planners and stripe trackers are ready to print.',
    },
    {
      q: 'Can I share it with my instructors?',
      a: 'This purchase is for your own use as an instructor. Sharing it across several schools, redistributing it or reselling it is not included in this offer.',
    },
    { q: 'Is it a subscription?', a: 'No. One payment of $27 USD for all 52 classes and the 5 bonuses.' },
    { q: 'How does the guarantee work?', a: 'You have 10 days to request a refund, no questions asked.' },
  ],
};

// ── 11. FINAL CTA ─────────────────────────────────────────────
export const finalCta = {
  eyebrow: 'FOR THE NEXT “WHAT DO I TEACH THE KIDS TONIGHT?”',
  title: 'WALK ONTO THE MAT WITH TONIGHT’S CLASS READY.',
  text: 'Open this week’s class. Prepare in 5 minutes. Teach a class the kids want to come back to, every week of the year.',
  microcopy: '52 complete classes + 5 bonuses · $27 USD, one-time payment',
};

// ── 12. FOOTER ──────────────────────────────────────────────────
export const footer = {
  line: 'MARTIAL ARTS KIDS CLASS READY™ · Class material for instructors. Always follow your style’s rules and your school’s safety policies.',
  supportLabel: 'Support:',
  supportEmail: 'support@stockdigital.shop',
  copyright: '© 2026',
};
